import type { Except, Optional } from '@festina-life/core'
import cx from 'classnames'
import type { ReactNode } from 'react'
import Markdown, { type Options } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

import { useDocumentationSiteLinkTransformer } from '../../hooks/useDocumentationSiteLinkTransformer'
import { MarkdownCode } from './MarkdownCode'
import { MarkdownMedia } from './MarkdownMedia'

// Custom link component

type CustomLinkType = {
  href?: string
  children?: ReactNode
}

const CustomLink = ({ href, children }: CustomLinkType) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

export type MarkdownViewerProps = Except<Options, 'children'> & {
  children?: Optional<string>
  className?: Optional<string>
  shouldOpenLinksInCurrentTab?: Optional<boolean>
  shouldTransformLinksToDocSite?: Optional<boolean>
  isTrusted?: Optional<boolean>
}

export const MarkdownViewer = ({
  children,
  className,
  shouldOpenLinksInCurrentTab,
  shouldTransformLinksToDocSite,
  isTrusted,
}: MarkdownViewerProps) => {
  const documentationTransformer = useDocumentationSiteLinkTransformer()

  return (
    <div
      className={cx(
        '[&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:py-2 prose-sm dark:prose-invert text-base! [&_pre]:p-0 [&_table]:text-base [&_td]:align-top',
        className,
      )}
    >
      <Markdown
        components={{
          img: MarkdownMedia,
          code: MarkdownCode,
          ...(!shouldOpenLinksInCurrentTab ? { a: CustomLink } : {}),
        }}
        remarkPlugins={[remarkGfm]}
        // Rehype raw is required for safe HTML rendering. Should only be used if the markdown is trusted.
        rehypePlugins={isTrusted ? [rehypeRaw] : []}
      >
        {shouldTransformLinksToDocSite
          ? documentationTransformer(children)
          : children}
      </Markdown>
    </div>
  )
}
